import { Alert, DatePicker, Form, Input, Tag, Typography } from 'antd';
import { FormProps, SelectProps } from 'antd/lib';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { memo, useCallback, useMemo } from 'react';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanySelectField from '@/components/general/CompanySelectField';
import { ADMIN_URL, MACHINES_URLS } from '@/constants/urls';
import { SERVICE_ORDER_STATUS, ServiceOrderTypeString } from '@/model/ServiceOrder/ServiceOrder';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ServiceOrderServiceDetailProps {
  formProps: FormProps;
  selectedCompany: number;
  technicianSelectProps: SelectProps;
  companySelectProps: SelectProps;
  initialTickets: number[];
  ticketsSelectProps: SelectProps;
  ticketMachineOptions: { label: string; value: number }[];
  ticketMachineOptionsLoading: boolean;
  companyContactsSelectProps: SelectProps;
  companyContactRules: any;
  createdAutomatically: boolean;
}

const ServiceOrderServiceDetail = (props: ServiceOrderServiceDetailProps) => {
  const { serviceOrderRepository } = useRepositoriesContext();
  const {
    formProps,
    selectedCompany,
    technicianSelectProps,
    companySelectProps,
    initialTickets,
    ticketsSelectProps,
    ticketMachineOptions,
    ticketMachineOptionsLoading,
    companyContactsSelectProps,
    companyContactRules,
    createdAutomatically,
  } = props;
  const lockedTicketsId = useMemo(() => {
    return initialTickets?.length ? initialTickets : [];
  }, [initialTickets]);
  const selectedTickets = Form.useWatch('tickets', formProps.form);
  // Bloquea el toggle desde el dropdown deshabilitando opciones ya seleccionadas.
  const ticketsOptionsWithSelectionLocked = useMemo(
    () =>
      (ticketsSelectProps?.options ?? []).map((option: any) => ({
        ...option,
        disabled: option?.disabled || lockedTicketsId.includes(option?.value),
      })),
    [lockedTicketsId, ticketsSelectProps?.options],
  );
  // Render del tag con el primer ticket no removible.
  const renderTicketTag = useCallback(
    (tagProps: any) => {
      const { label, value, closable, onClose } = tagProps;
      const isLocked =
        lockedTicketsId !== null && lockedTicketsId.length > 0 && lockedTicketsId.includes(value);

      return (
        <Tag
          className="ServiceOrderFormTicketTag"
          closable={closable && !isLocked}
          onClose={onClose}>
          {label}
        </Tag>
      );
    },
    [lockedTicketsId],
  );
  // Campo de empresa
  const companyField = (
    <CompanySelectField
      name="company"
      label={t('Empresa')}
      formProps={formProps}
      selectProps={{
        ...companySelectProps,
        loading: companySelectProps.loading,
        allowClear: true,
        showSearch: true,
        disabled: createdAutomatically,
      }}
      rules={[
        {
          required: true,
          message: t('La {{field}} es requerida', { field: t('Empresa') }),
        },
      ]}
      required
    />
  );

  return (
    <>
      <div className="ServiceOrderFormRow">
        <Form.Item
          label={t('Técnico')}
          name={'technician'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('técnico') }),
            },
          ]}>
          <AntdSelect {...technicianSelectProps} disabled={createdAutomatically} />
        </Form.Item>
        {companyField}
      </div>

      <Form.Item label={'Tickets'}>
        <Form.Item
          noStyle
          name={'tickets'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: 'ticket' }),
            },
            {
              validator: async (_, value) => {
                if (!value?.length) {
                  return Promise.resolve();
                }

                if (Array.isArray(value)) {
                  const lastTicketId = value[value.length - 1]; // no mutar
                  const ticketHasServiceOrder =
                    await serviceOrderRepository.checkServiceOrderForTicket(
                      lastTicketId,
                      formProps?.initialValues?.id,
                    );

                  if (ticketHasServiceOrder) {
                    return Promise.reject(
                      `${t(
                        'El ticket seleccionado ya tiene una orden de servicio asociada',
                      )} - Ticket ${lastTicketId}`,
                    );
                  }
                }
              },
            },
          ]}>
          <AntdSelect
            {...ticketsSelectProps}
            onSelect={(v, option) => {
              if (initialTickets.includes(v)) {
                return;
              } else {
                ticketsSelectProps?.onSelect?.(v, option);
              }
            }}
            onChange={v => {
              if (initialTickets.includes(v)) {
                return;
              } else {
                ticketsSelectProps?.onChange?.(v);
              }
            }}
            mode="multiple"
            disabled={!selectedCompany}
            tagRender={renderTicketTag}
            options={ticketsOptionsWithSelectionLocked}
          />
        </Form.Item>
        {selectedTickets != null && initialTickets?.length != selectedTickets?.length && (
          <Alert
            style={{ margin: '4px 0' }}
            className="ServiceOrderFormTicketsAlert"
            showIcon
            type="warning"
            message={t(
              'Una vez guardados los tickets no podrán ser eliminados de la orden de servicio',
            )}
          />
        )}
      </Form.Item>

      <Form.Item label={t('Máquinas involucradas')}>
        {ticketMachineOptionsLoading ? (
          <Typography.Text type="secondary">{t('Cargando máquinas')}...</Typography.Text>
        ) : ticketMachineOptions.length ? (
          <div className="ServiceOrderFormMobilesList">
            {ticketMachineOptions.map(machine => (
              <Tag
                key={machine.value}
                className="ServiceOrderFormMobileTag"
                onClick={() =>
                  window.open(`${ADMIN_URL}/${MACHINES_URLS.ADMIN_MOVIL}/${machine.value}`)
                }>
                {machine.label ? `${machine.label} (${machine.value})` : `#${machine.value}`}
              </Tag>
            ))}
          </div>
        ) : (
          <Typography.Text type="secondary">
            {t('Seleccione tickets para ver las máquinas asociadas')}
          </Typography.Text>
        )}
      </Form.Item>
      <Form.Item hidden name={'moviles'}>
        <Input />
      </Form.Item>

      <div className="ServiceOrderFormRow">
        <Form.Item
          label={t('Tipo de servicio')}
          name={'type'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('tipo de servicio') }),
            },
          ]}>
          <AntdSelect
            options={Object.keys(ServiceOrderTypeString).map(type => ({
              label: t(ServiceOrderTypeString[type]),
              value: parseInt(type, 10),
            }))}
            disabled={createdAutomatically}
          />
        </Form.Item>
        <Form.Item
          label={t('Fecha de servicio')}
          name={'service_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}
          rules={[
            {
              required:
                formProps?.initialValues?.status !== SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
              message: t('La {{field}} es requerida', { field: t('fecha de servicio') }),
            },
          ]}>
          <DatePicker className={'ServiceOrderFormDatePicker'} format={'L'} />
        </Form.Item>
      </div>

      <Form.Item
        label={t('Contacto con quien se habló el servicio')}
        name={'contact_data'}
        rules={companyContactRules}>
        <AntdSelect {...companyContactsSelectProps} />
      </Form.Item>
      <Form.Item
        label={t('Descripción')}
        name={'description'}
        rules={[
          { required: true, message: t('La {{field}} es requerida', { field: t('descripción') }) },
        ]}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} showCount />
      </Form.Item>
    </>
  );
};

export default memo(ServiceOrderServiceDetail);
